// src/pages/widgets/DocumentDetailWidget.js

import React, { useState } from 'react';
import ReUploadWidget from './ReUploadWidget';
import { apiService } from '../services/api'; 
import './style/DocumentDetailWidget.css';

const DocumentDetailWidget = ({ document, onClose, onRefresh }) => {
  const [showReUpload, setShowReUpload] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  if (!document) return null;

  // --- CHECK ARCHIVE STATUS ---
  const isArchived = document.is_archived === 1 || document.is_archived === true || document.is_archived === '1';

  // Normalize alert days
  const actualAlertDays = document.alertDays !== undefined ? document.alertDays : document.alert_days_before;
  
  // --- FIX: Ensure this is a strict Boolean ---
  // Previously, if actualAlertDays was 0, this variable became 0, causing React to render "0"
  const hasAlertsEnabled = parseInt(actualAlertDays || 0, 10) > 0;

  const getExpiryLabel = (expireDateString) => {
    if (!expireDateString) return null;
    const today = new Date();
    const expireDate = new Date(expireDateString);
    today.setHours(0, 0, 0, 0);
    expireDate.setHours(0, 0, 0, 0);
    const diffTime = expireDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return { label: 'EXPIRED', className: 'expired' };
    if (diffDays >= 0 && diffDays <= 30) return { label: 'EXPIRING IN 1 MONTH', className: 'expiring' };
    if (diffDays > 30 && diffDays <= 60) return { label: 'EXPIRING IN 2 MONTHS', className: 'expiring-warning' };
    return null; 
  };

  const formatFileSize = (sizeInBytes) => {
    if (!sizeInBytes) return '0 B';
    if (typeof sizeInBytes === 'string' && isNaN(Number(sizeInBytes))) return sizeInBytes;
    const bytes = Number(sizeInBytes);
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatAlertTiming = (days) => {
    if (days === null || days === undefined || days === '') return 'None';
    const numDays = parseInt(days, 10);
    if (isNaN(numDays) || numDays === 0) return 'None';
    
    if (numDays % 30 === 0) {
        const months = Math.round(numDays / 30);
        return `${months} Month${months > 1 ? 's' : ''} Before`;
    }
    return `${numDays} Day${numDays > 1 ? 's' : ''} Before`;
  };

  // --- HANDLE ARCHIVE ---
  const handleArchiveDocument = async () => {
    const confirmArchive = window.confirm(
      "Are you sure you want to move this document to the Archive?\n\nIt will be removed from the active list."
    );

    if (confirmArchive) {
      setIsUploading(true); 
      try {
        await apiService.archiveDocument(document.id);
        alert("Document moved to archive.");
        if (onRefresh) await onRefresh(); 
        onClose(); 
      } catch (error) {
        console.error("Archive failed", error);
        alert("Failed to archive: " + (error.response?.data?.message || error.message));
      } finally {
        setIsUploading(false);
      }
    }
  };

  // --- HANDLE UNARCHIVE ---
  const handleUnarchiveDocument = async () => {
    const confirmRestore = window.confirm(
      "Are you sure you want to restore this document?\n\nIt will appear back in the main active list."
    );

    if (confirmRestore) {
      setIsUploading(true);
      try {
        await apiService.unarchiveDocument(document.id);
        alert("Document restored to main list.");
        if (onRefresh) await onRefresh();
        onClose();
      } catch (error) {
        console.error("Restore failed", error);
        alert("Failed to restore: " + (error.response?.data?.message || error.message));
      } finally {
        setIsUploading(false);
      }
    }
  };

  // --- HANDLE UPLOAD COMPLETE ---
  const handleUploadComplete = async (
    file, 
    newStartDate, 
    newExpireDate, 
    organisationId, 
    newAlertTiming, 
    newName, 
    newCategory, 
    newOrgName,  
    newCatName,  
    newAlertFrequency, 
    archiveOld         
  ) => {
    setIsUploading(true);
    try {
      if (file) {
        const formData = new FormData();
        formData.append('organisationName', newOrgName); 
        formData.append('categoryName', newCatName);
        formData.append('file', file);
        formData.append('displayName', newName || file.name);
        formData.append('organisationId', organisationId);
        formData.append('category', newCategory || 'General');
        
        if (newStartDate) formData.append('startDate', newStartDate);
        if (newExpireDate) formData.append('expiryDate', newExpireDate);
        if (newAlertTiming) formData.append('alertTiming', newAlertTiming);
        if (newAlertFrequency) formData.append('alertFrequency', newAlertFrequency); 
        
        formData.append('archiveOld', archiveOld); 
        formData.append('id', document.id); 
        formData.append('oldOrganisationName', document.organisation);
        formData.append('oldCategoryName', document.category);
        formData.append('oldFileName', document.name); 
        
        await apiService.updateDocument(document.id, formData); 
      } 
      else {
        const updateData = {
          id: document.id, 
          organisationId: organisationId,
          category: newCategory, 
          organisationName: newOrgName, 
          categoryName: newCatName,
          oldOrganisationName: document.organisation, 
          oldCategoryName: document.category,
          oldFileName: document.name, 
          startDate: newStartDate,
          expiryDate: newExpireDate,
          alertTiming: newAlertTiming,
          alertFrequency: newAlertFrequency, 
          displayName: newName, 
          archiveOld: archiveOld 
        };

        await apiService.updateDocumentMetadata(updateData);
      }
      
      alert("Document updated successfully!");
      setShowReUpload(false);
      if (onRefresh) await onRefresh();
      onClose(); 
    } catch (error) {
      console.error("Update failed", error);
      alert("Update failed: " + (error.response?.data?.message || error.message));
    } finally {
      setIsUploading(false);
    }
  };

  const expiryInfo = getExpiryLabel(document.expireDate);

  return (
    <>
      <div className="widget-overlay">
        <div className="widget-panel-doc-detail">
          <div className="widget-header-doc-detail">
            <h2 className="widget-title-doc-detail">
              {isArchived ? '📦 Archived Document' : '📄 Document Details'}
            </h2>
            <button className="close-btn" onClick={onClose}>&times;</button>
          </div>

          <div className="widget-content-doc-detail">
            <DetailRow label="Name" value={document.name} className="full-width" />
            <DetailRow label="Organisation" value={document.organisation} />
            <DetailRow label="Category" value={document.category} />
            <DetailRow label="Format" value={document.format} />
            <DetailRow label="Size" value={formatFileSize(document.size)} />
            <DetailRow label="Uploaded By" value={document.uploadedBy} />
            <DetailRow label="Uploaded Date" value={formatDateTime(document.uploadedDate)} />
            <DetailRow label="Start Date" value={document.startDate ? formatDate(document.startDate) : 'N/A'} />
            <DetailRow 
              label="Expire Date" 
              value={
                <div className="detail-expiry-wrapper">
                  <span>{document.expireDate ? formatDate(document.expireDate) : 'Never'}</span>
                  {expiryInfo && <span className={`detail-tag ${expiryInfo.className}`}>{expiryInfo.label}</span>}
                </div>
              } 
            />
            
            <DetailRow 
              label="Expiry Alert" 
              value={formatAlertTiming(actualAlertDays)} 
              className="full-width" 
            />
            
            {/* 
               The fix ensures hasAlertsEnabled is strictly true or false.
               If it was 0, React would render "0". Now it renders nothing.
            */}
            {hasAlertsEnabled && (
              <DetailRow label="Alert Frequency" value={document.alert_frequency || 'Weekly'} />
            )}
            
            <DetailRow 
              label="Status" 
              value={
                <span style={{
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '0.85rem',
                  fontWeight: 'bold',
                  backgroundColor: isArchived ? '#fff1f0' : '#f6ffed',
                  color: isArchived ? '#cf1322' : '#389e0d',
                  border: `1px solid ${isArchived ? '#ffa39e' : '#b7eb8f'}`
                }}>
                  {isArchived ? 'Archived' : 'Active'}
                </span>
              } 
            />
          </div>

          <div className="widget-footer">
            {isArchived ? (
              <button 
                className="action-btn" 
                style={{ backgroundColor: '#52c41a', color: 'white', border: 'none', marginRight: 'auto' }}
                onClick={handleUnarchiveDocument}
              >
                Restore to Main List
              </button>
            ) : (
              <button 
                className="action-btn" 
                style={{ backgroundColor: '#ff4d4f', color: 'white', border: 'none', marginRight: 'auto' }}
                onClick={handleArchiveDocument}
              >
                Archive
              </button>
            )}

            {!isArchived && (
               <button className="action-btn" onClick={() => setShowReUpload(true)}>Edit Document</button>
            )}

            <button className="primary-btn" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>

      {showReUpload && (
        <ReUploadWidget 
          documentName={document.name}
          currentCategory={document.category}
          currentStartDate={document.startDate}
          currentExpireDate={document.expireDate}
          currentAlertDays={actualAlertDays} 
          currentAlertFrequency={document.alert_frequency} 
          currentOrganisation={document.organisation} 
          currentOrganisationId={document.organisationId} 
          onClose={() => setShowReUpload(false)}
          onUpload={handleUploadComplete}
        />
      )}
      
      {isUploading && (
        <div className="widget-overlay" style={{zIndex: 2000, color: 'white'}}>
          <h2>Processing...</h2>
        </div>
      )}
    </>
  );
};

const DetailRow = ({ label, value, className = '' }) => (
  <div className={`detail-row ${className}`}>
    <span className="detail-label">{label}</span>
    <div className="detail-value">{value}</div>
  </div>
);

export default DocumentDetailWidget;
